// Payments API — typed-обёртки над 4 endpoints /api/payments/* (V12 U-35, волна R66-R67).
// Контракт (backend PaymentDto/PaymentCreateRequest/PaymentUpdateRequest, §3.4):
//   - GET /api/payments?page&size&status&source&companyId|contactId|dealId|leadId&entityType&entityId
//     → PaymentListResponse { content, total, page, size } (синонимы entityType+entityId);
//   - POST /api/payments (manual): entityType+entityId обязательны (ровно 1 сущность);
//     backend ставит source=MANUAL, status=PENDING, currency=RUB (54-ФЗ);
//   - PATCH /api/payments/{id}: status/фискальные поля + expectedUpdatedAt (409 stale);
//   - POST /api/payments/receipt: multipart (file + entityType + entityId) → 201 Payment
//     (source=QR, status=CONFIRMED) | 422 «Не удалось распознать чек — создайте платёж вручную».

import { apiClient } from './client';
import type { Page } from './tasks';

/** Статус платежа (backend PaymentStatus, §3.4: chk_payments_status). */
export type PaymentStatus = 'PENDING' | 'CONFIRMED' | 'FAILED';

/** Источник платежа (backend PaymentSource). */
export type PaymentSource = 'QR' | 'MANUAL';

/** Тип сущности-привязки платежа (backend PaymentEntityType; ровно одна). */
export type PaymentEntityType = 'COMPANY' | 'CONTACT' | 'DEAL' | 'LEAD';

/** DTO платежа (wire-формат backend PaymentDto §3.4; имена привязанных сущностей из JOIN'ов). */
export interface Payment {
  id: string;
  /** Сквозной номер #N (BIGSERIAL, канон V27). */
  paymentNumber: number | null;
  companyId: string | null;
  /** Название компании (LEFT JOIN, nullable). */
  companyName: string | null;
  contactId: string | null;
  /** ФИО контакта (LEFT JOIN, nullable). */
  contactName: string | null;
  dealId: string | null;
  /** Заголовок сделки (LEFT JOIN, nullable). */
  dealTitle: string | null;
  leadId: string | null;
  /** Сумма платежа (DECIMAL(15,2)). */
  amount: number;
  /** ISO 4217 (54-ФЗ: RUB по умолчанию). */
  currency: string;
  /** Дата/время платежа (ISO Instant; из QR чека или вручную). */
  paymentDate: string;
  /** Фискальный № документа (ФД, i=… из QR; nullable). */
  fiscalDocNumber: string | null;
  /** Фискальный признак (ФП, fp=…; nullable). */
  fiscalSign: string | null;
  /** № фискального накопителя (ФН, fn=…; nullable). */
  fnNumber: string | null;
  /** № ККТ (n=…; nullable). */
  kktNumber: string | null;
  /** «№ чека в смене» (вручную; nullable). */
  receiptNumber: string | null;
  /** Ссылка ОФД (nullable; для V12.2). */
  ofdUrl: string | null;
  status: PaymentStatus;
  source: PaymentSource;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Фильтры списка платежей (синонимы entityType+entityId — как id-фильтры, §3.4). */
export interface PaymentFilters {
  page?: number;
  size?: number;
  status?: PaymentStatus;
  source?: PaymentSource;
  companyId?: string;
  contactId?: string;
  dealId?: string;
  leadId?: string;
  /** Синоним id-фильтра: разворачивается backend'ом в companyId/contactId/dealId/leadId. */
  entityType?: PaymentEntityType;
  entityId?: string;
}

/** Тело POST /api/payments (manual; source/status ставит backend). */
export interface PaymentCreateRequest {
  amount: number;
  /** 3 буквы UPPERCASE; null → RUB (54-ФЗ). */
  currency?: string;
  /** ISO Instant; null → now() на backend. */
  paymentDate?: string;
  fiscalDocNumber?: string | null;
  fiscalSign?: string | null;
  fnNumber?: string | null;
  kktNumber?: string | null;
  receiptNumber?: string | null;
  ofdUrl?: string | null;
  /** Ровно одна привязка (NOT NULL). */
  entityType: PaymentEntityType;
  entityId: string;
}

/** Тело PATCH /api/payments/{id} (partial; optimistic lock через expectedUpdatedAt). */
export interface PaymentUpdateRequest {
  status?: PaymentStatus;
  fiscalDocNumber?: string | null;
  fiscalSign?: string | null;
  fnNumber?: string | null;
  kktNumber?: string | null;
  receiptNumber?: string | null;
  ofdUrl?: string | null;
  expectedUpdatedAt: string;
}

/** Payments API. */
export const paymentsApi = {
  /** GET /api/payments?filters → Page<Payment>. */
  async list(filters: PaymentFilters = {}): Promise<Page<Payment>> {
    const { data } = await apiClient.get<Page<Payment>>('/api/payments', { params: filters });
    return data;
  },

  /** POST /api/payments → 201 Payment (manual: source=MANUAL, status=PENDING). */
  async create(payload: PaymentCreateRequest): Promise<Payment> {
    const { data } = await apiClient.post<Payment>('/api/payments', payload);
    return data;
  },

  /** PATCH /api/payments/{id} → Payment (optimistic lock; 409 на stale version). */
  async update(id: string, payload: PaymentUpdateRequest): Promise<Payment> {
    const { data } = await apiClient.patch<Payment>(`/api/payments/${id}`, payload);
    return data;
  },

  /**
   * POST /api/payments/receipt — загрузка чека (multipart: file + entityType + entityId).
   * <p>
   * Content-Type принудительно multipart, чтобы axios добавил boundary — иначе
   * дефолтный `application/json` ломает FormData-контракт backend'а.
   */
  async uploadReceipt(formData: FormData): Promise<Payment> {
    const { data } = await apiClient.post<Payment>('/api/payments/receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};
